import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Tag, TagProductAssociation } from '../../actions/tagActions/types';
import { createTagAction, deleteTagAction, getProductsByTagIdAction, getTagsAction, searchTagsAction, updateTagAction, searchTagsCachedAction } from '../../actions/tagActions';

export interface TagReducerType {
    pending: boolean;
    tags: Array<Tag>;
    totalCount: number;
    searchCache?: Record<string, { results: Tag[]; currentCount: number; fetchedAt: number }>;
    productsByTagId: Record<number, { pending: boolean; products: TagProductAssociation[]; fetchedAt?: number }>;
}

const initialState: TagReducerType = {
    pending: false,
    tags: [],
    totalCount: 0,
    searchCache: {},
    productsByTagId: {},
};

export const tagSlice = createSlice({
    name: 'tags',
    initialState,
    reducers: {
        removeProductsFromTagCache: (
            state,
            action: PayloadAction<{ tagId: number; productIds: number[] }>,
        ) => {
            const { tagId, productIds } = action.payload;
            const entry = state.productsByTagId[tagId];
            if (entry) {
                entry.products = entry.products.filter(
                    (p) => !productIds.includes(p.id),
                );
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTagsAction.pending, (state) => {
                state.pending = true;
            })
            .addCase(getTagsAction.fulfilled, (state, action) => {
                state.pending = false;
                if (action.payload) {
                    state.tags = action.payload.data;
                    state.totalCount = action.payload.data.length;
                }
            })
            .addCase(getTagsAction.rejected, (state) => {
                state.pending = false;
            })
            .addCase(searchTagsAction.pending, (state) => {
                state.pending = true;
            })
            .addCase(searchTagsAction.fulfilled, (state, action) => {
                state.pending = false;
                if (action.payload) {
                    state.tags = action.payload.data.items;
                    state.totalCount = action.payload.data.currentCount;
                }
            })
            .addCase(searchTagsAction.rejected, (state) => {
                state.pending = false;
            })
            .addCase(searchTagsCachedAction.fulfilled, (state, action) => {
                state.pending = false;
                if (action.payload && action.meta?.arg) {
                    const q = (action.meta.arg.searchString || '').toLowerCase();
                    const items = action.payload.data.items || [];
                    const currentCount = action.payload.data.currentCount || items.length;
                    state.searchCache ??= {};
                    state.searchCache[q] = { results: items, currentCount, fetchedAt: Date.now() };
                }
            })
            .addCase(createTagAction.pending, (state) => {
                state.pending = true;
            })
            .addCase(createTagAction.fulfilled, (state) => {
                state.pending = false;
            })
            .addCase(createTagAction.rejected, (state) => {
                state.pending = false;
            })
            .addCase(updateTagAction.pending, (state) => {
                state.pending = true;
            })
            .addCase(updateTagAction.fulfilled, (state, action) => {
                state.pending = false;
                if (action.payload) {
                    const updated = action.payload.data;
                    const index = state.tags.findIndex((t) => t.id === updated.id);
                    if (index !== -1) {
                        state.tags[index] = updated;
                    }
                }
            })
            .addCase(updateTagAction.rejected, (state) => {
                state.pending = false;
            })
            .addCase(deleteTagAction.pending, (state) => {
                state.pending = true;
            })
            .addCase(deleteTagAction.fulfilled, (state, action) => {
                state.pending = false;
                state.tags = state.tags.filter((t) => t.id !== action.meta.arg);
                state.totalCount = Math.max(0, state.totalCount - 1);
            })
            .addCase(deleteTagAction.rejected, (state) => {
                state.pending = false;
            })
            .addCase(getProductsByTagIdAction.pending, (state, action) => {
                const tagId = action.meta.arg;
                state.productsByTagId[tagId] = { pending: true, products: [] };
            })
            .addCase(getProductsByTagIdAction.fulfilled, (state, action) => {
                const { tagId, products } = action.payload;
                state.productsByTagId[tagId] = {
                    pending: false,
                    products,
                    fetchedAt: Date.now(),
                };
            })
            .addCase(getProductsByTagIdAction.rejected, (state, action) => {
                const tagId = action.meta.arg;
                state.productsByTagId[tagId] = { pending: false, products: [] };
            });
    },
});

export const { removeProductsFromTagCache } = tagSlice.actions;

export default tagSlice.reducer;
